<template>
  <div>
    <div class="relative px-20">
      <!-- Header with navigation buttons aligned -->
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-semibold">Paket Seçimi</h2>

        <div class="flex space-x-2">
          <!-- Left navigation button -->
          <button
            @click="prevSlide"
            class="bg-white rounded-full shadow-lg p-2 hover:bg-gray-100 transition-opacity duration-300"
            :class="{
              'opacity-30 cursor-not-allowed': currentSlide === 0,
              'opacity-100 cursor-pointer': currentSlide > 0,
            }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Right navigation button -->
          <button
            @click="nextSlide"
            class="bg-white rounded-full shadow-lg p-2 hover:bg-gray-100 transition-opacity duration-300"
            :class="{
              'opacity-30 cursor-not-allowed': isLastSlide,
              'opacity-100 cursor-pointer': !isLastSlide,
            }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        class="packages-slider overflow-hidden relative"
        ref="sliderContainer"
      >
        <div
          class="flex transition-transform duration-300 ease-in-out mt-6"
          ref="sliderContent"
          :style="{
            transform: `translateX(-${currentSlide * (slideWidth + 24)}px)`,
          }"
        >
          <div
            v-for="(packagex, index) in packageStore.packages"
            :key="index"
            class="flex-shrink-0 px-3"
            :style="{ width: `${100 / visibleSlides}%` }"
          >
            <pricing-single-box
              :packagex="packagex"
              button-text="Seç"
              :isSelected="selectedPackage === packagex"
              @on-yearly-change="onYearlyChange"
              @click="selectPackage(packagex)"
              class="w-full h-full"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, ref, computed, onMounted, onUnmounted, watch } from "vue";
import PricingSingleBox from "@/components/LandingLayout/landing-compoments/PricingSingleBox.vue";
import { usePackageStore } from "@/stores/packageStore";

const packageStore = usePackageStore();

const emit = defineEmits(["onYearlyChange", "onSelectedPackageChange"]);
const selectedPackage = ref(null);
const sliderContainer = ref(null);
const sliderContent = ref(null);
const currentSlide = ref(0);
const slideWidth = ref(0);
const visibleSlides = 3; // Always show 3 items

const selectPackage = (packagex) => {
  emit("onSelectedPackageChange", packagex);
  selectedPackage.value = packagex;
};

const onYearlyChange = (isYearly) => {
  emit("onYearlyChange", isYearly);
};

const calculateSlideWidth = () => {
  if (sliderContainer.value) {
    slideWidth.value = sliderContainer.value.clientWidth / visibleSlides - 24;
  }
};

const isLastSlide = computed(() => {
  if (!packageStore.packages || packageStore.packages.length <= visibleSlides)
    return true;
  return currentSlide.value >= packageStore.packages.length - visibleSlides;
});

const nextSlide = () => {
  if (!isLastSlide.value) {
    currentSlide.value++;
  }
};

const prevSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--;
  }
};

const handleResize = () => {
  calculateSlideWidth();
};

onMounted(() => {
  calculateSlideWidth();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

// Recalculate on package store changes
watch(
  () => packageStore.packages,
  () => {
    calculateSlideWidth();
    currentSlide.value = 0; // Reset to first slide when packages change
  }
);
</script>

<style scoped>
.packages-slider {
  width: 100%;
  position: relative;
}

/* Add page indicators if desired */
.slider-indicators {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 8px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: background-color 0.3s;
}

.indicator.active {
  background-color: #4b5563;
  transform: scale(1.25);
}
</style>
